import { isArray } from 'lodash-es'

import { useCheckPlanFeatures } from '@/composables/useCheckPlanFeature'
import { useConfirmDialog } from '@/composables/useConfirmDialog'

import { tokenService } from '@/utils/auth/tokenService'
import { getRedirectToFrontendRoute, showRedirectError } from '@/utils/common/redirectToFrontend'
import { getSavedRedirectPath, clearRedirectPath, saveCurrentPath } from '@/utils/storage/redirectPath'
import { setNativeRegistration, setPreviousRouteName } from '@/utils/storage/registrationRoutes'

import { registerAPI } from '@/APIs'
import { REDIRECT_ERROR } from '@/constant/client/authErrors'
import { LOCAL_STORAGE_KEYS } from '@/constant/storage'
import appStore from '@/stores'

import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'

type NavigationGuardReturn = void | boolean | RouteLocationRaw
type GuardHandler = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  router?: Router
) => Promise<NavigationGuardReturn> | NavigationGuardReturn

// ========== 1. 登入守衛 ==========
/**
 * 處理登入頁面的重定向
 * 如果用戶已登入，重定向到前台首頁
 * 優先使用 session storage 中儲存的重定向路徑（用於登出後返回）
 */
const handleLoginRedirect: GuardHandler = async (to) => {
  if (to.name !== 'login') {
    return undefined
  }

  const authStore = appStore.useAuthStore
  if (tokenService.getAccessToken()) {
    try {
      await authStore.getUserInfo()
    } catch (error) {
      // 未捕捉的例外會讓整條導航無聲中斷，失敗時停留在登入頁
      console.error('Failed to get user info:', error)
      return undefined
    }

    // 優先檢查是否有儲存的重定向路徑（Token 刷新失敗登出時儲存）
    const savedPath = getSavedRedirectPath()
    if (savedPath) {
      clearRedirectPath()
      // 使用 window.location.href 確保完整重新載入頁面狀態
      window.location.href = savedPath
      return false // 阻止路由守衛繼續執行
    }

    // 沒有儲存路徑，使用預設的前台頁面重定向
    return await getRedirectToFrontendRoute()
  }
  return undefined
}

// ========== 2. 註冊邀請守衛 ==========
/**
 * 驗證註冊邀請碼
 */
const handleInvitationValidation: GuardHandler = async (to, _from, router?: Router) => {
  const invitationCode = to.params.invitation as string
  const invitationEmail = to.query.email as string
  const invitationChecksum = to.query.check as string

  const isInvitationRoute = to.name === 'register-invitation' && invitationCode && invitationEmail && invitationChecksum

  if (!isInvitationRoute) {
    return undefined
  }

  setNativeRegistration(invitationEmail, invitationChecksum)

  try {
    const { data } = await registerAPI.validateInvitationCode(invitationCode, invitationEmail, invitationChecksum)

    if (!data.status) {
      const { alert } = useConfirmDialog()
      alert({
        title: '錯誤',
        text: data.message || '',
        confirmButtonText: '確定',
        onConfirm: () => {
          router?.push({ name: 'login' })
        }
      })
    }
    return undefined
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || error.message
    console.error(errorMsg)
    return { name: 'not-found' }
  }
}

// ========== 3. 身份驗證守衛 ==========
/**
 * 檢查路由是否需要身份驗證和角色權限
 */
const handleAuthentication: GuardHandler = async (to) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  if (!requiresAuth) {
    return undefined
  }

  const authStore = appStore.useAuthStore

  if (!tokenService.getAccessToken()) {
    saveCurrentPath(true)
    return { name: 'login' }
  }

  if (!authStore.hasUserInfo()) {
    try {
      await authStore.getUserInfo()
    } catch (error) {
      // 未捕捉的例外會讓整條導航無聲中斷，失敗時導回登入頁並保留原路徑
      console.error('Failed to get user info:', error)
      saveCurrentPath(true)
      return { name: 'login' }
    }
  }

  if (to.meta.requiresRole && isArray(to.meta.requiresRole)) {
    if (!authStore.handlePermission(to.meta.requiresRole)) {
      return await getRedirectToFrontendRoute()
    }
  }
  return undefined
}

// ========== 4. 頁面歷史記錄守衛 ==========
/**
 * 記錄來源頁面，用於 Dashboard 返回功能
 */
const handlePageHistory: GuardHandler = (to, from) => {
  const isDashboardRoute = to.name === 'single-url-analytics' || to.name === 'group-url-analytics'

  if (isDashboardRoute && from.name && from.name !== to.name) {
    setPreviousRouteName(from.name.toString())
  }
  return undefined
}

// ========== 5. 前台頁面初始化守衛 ==========
/**
 * 初始化前台頁面的公司和組織資料
 */
const handleFrontendPageInit: GuardHandler = async (to, from) => {
  if (!to.path.startsWith('/frontend')) {
    return undefined
  }

  // 相同路由且公司/組織未變，跳過初始化
  if (shouldSkipFrontendInit(to, from)) {
    return undefined
  }

  const shortUrlStore = appStore.useShortUrlStore
  const feCompanyStore = appStore.useFECompanyStore

  const companyUuid = to.query.companyUuid as string
  const orgId = to.query.orgId as string

  shortUrlStore.setCompanyUuId(companyUuid)
  await feCompanyStore.handleGetCompanyPlanFeatures(companyUuid)
  const isValidCompanyId = await feCompanyStore.handleGetOrganizations(companyUuid)

  // 驗證公司
  if (!isValidCompanyId) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.COMPANY_UUID)
    return { name: 'not-found' }
  }

  // 設置組織：orgId 需屬於該公司的組織清單，否則視為無效（如已被移出組織的舊書籤），改用第一個組織
  const orgOptions = feCompanyStore.feOrganizations?.options || []
  const isValidOrgId = !!orgId && orgOptions.some((org) => org.companyOrganizationUuid === orgId)
  const targetOrgId = isValidOrgId ? orgId : (orgOptions[0]?.companyOrganizationUuid as string)

  if (!targetOrgId) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.COMPANY_UUID)
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ORG_ID)

    const redirectRoute = await getRedirectToFrontendRoute()

    // getRedirectToFrontendRoute 失敗時已顯示對應錯誤彈窗，直接回傳避免重複彈窗
    if (!('query' in redirectRoute)) {
      return redirectRoute
    }

    // 重新挑選後仍是同一間公司，代表公司列表與組織清單兩支 API 資料不一致，再導向會無限迴圈，改走錯誤登出流程
    if (redirectRoute.query?.companyUuid === companyUuid) {
      showRedirectError(REDIRECT_ERROR.NO_ORGANIZATION)
      return { name: 'login' }
    }

    const { alert } = useConfirmDialog()
    alert({
      title: '警告',
      text: '選取的公司沒有組織',
      confirmButtonText: '確定'
    })
    return redirectRoute
  }

  shortUrlStore.setOrganizationId(targetOrgId)
  return undefined
}

// ========== 6. 功能權限守衛 ==========
/**
 * 檢查方案功能權限（如批次建立短網址、資料重疊分析）
 */
const handleFeaturePermission: GuardHandler = async (to) => {
  const { canBatchCreateShortUrls, canViewDataOverlapAnalysis } = useCheckPlanFeatures()

  if (to.meta.requiresBatchCreate && !canBatchCreateShortUrls.value) {
    return await getRedirectToFrontendRoute()
  }

  if (to.meta.requiresOverlapFeature && !canViewDataOverlapAnalysis.value) {
    return await getRedirectToFrontendRoute()
  }

  return undefined
}

// ========== 輔助函式 ==========
/**
 * 判斷是否應該跳過前台頁面初始化
 * 當路由、公司、組織都相同時，只有其他 query 參數變化（如日期），則跳過
 * @param to 目標路由
 * @param from 來源路由
 * @returns 是否跳過初始化
 */
const shouldSkipFrontendInit = (to: RouteLocationNormalized, from: RouteLocationNormalized): boolean => {
  return from.name === to.name && from.query.companyUuid === to.query.companyUuid && from.query.orgId === to.query.orgId
}

// ========== 守衛組合器 ==========
/**
 * 組合多個守衛函式，按順序執行
 * 如果任一守衛回傳導航結果，則立即回傳該結果
 * @param router 路由實例
 * @param guards 多個守衛函式
 * @returns 組合後的守衛函式
 */
const composeGuards =
  (router: Router, ...guards: GuardHandler[]): GuardHandler =>
  async (to, from) => {
    for (const guard of guards) {
      const result = await guard(to, from, router)
      if (result !== undefined) {
        return result
      }
    }
    return undefined
  }

/**
 * 設置路由守衛
 * 所有守衛按照定義順序依次執行
 * @param router 路由實例
 * @return void
 */
export const setupRouterGuards = (router: Router) => {
  const guardChain = composeGuards(
    router,
    handleLoginRedirect, // 1. 處理登入重定向
    handleInvitationValidation, // 2. 驗證註冊邀請碼
    handleAuthentication, // 3. 身份驗證和權限檢查
    handlePageHistory, // 4. 記錄頁面歷史
    handleFrontendPageInit, // 5. 初始化前台頁面數據
    handleFeaturePermission // 6. 檢查功能權限
  )

  router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    return await guardChain(to, from)
  })
}
